<template>
  <div>
    <div class="contact--profile">
      <div
        v-if="isContactLoading"
        class="contact--profile-loading text-center p-normal"
      >
        <spinner size="" />
        <span>{{ $t('CONTACT_PROFILE.LOADING') }}</span>
      </div>
      <div v-else class="contact--info">
        <div class="contact-info--header">
          <thumbnail
            v-if="showAvatar"
            :src="contact.thumbnail"
            size="56px"
            :username="contact.name"
            :status="contact.availability_status"
          />
          <woot-button
            v-if="showCloseButton"
            :icon="closeIconName"
            class="clear secondary close-button--rtl"
            @click="onPanelToggle"
          />
        </div>

        <div class="contact--details">
          <div v-if="showAvatar || isTicket" class="contact--name-wrap">
            <h3 class="sub-block-title contact--name">
              {{ contact.name }}
            </h3>
            <fluent-icon
              v-if="contact.created_at && !isTicket"
              v-tooltip="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              icon="info"
              size="14"
            />
            <a
              :href="contactProfileLink"
              class="fs-default"
              target="_blank"
              rel="noopener nofollow noreferrer"
            >
              <woot-button
                size="tiny"
                icon="open"
                variant="clear"
                color-scheme="secondary"
              />
            </a>
          </div>

          <div
            v-if="showAvatar && !isGroup"
            class="contact--status"
            :class="{ 'is-online': isOnline }"
          >
            <span class="status-dot" />
            {{ isOnline ? 'Online agora' : 'Offline' }}
          </div>

          <p v-if="additionalAttributes.description" class="contact--bio">
            {{ additionalAttributes.description }}
          </p>
          <social-icons :social-profiles="socialProfiles" />
          <div v-if="hasDescription">
            <div :class="className" v-html="descriptionFormatter" />
            <div
              v-if="isLong"
              class="description-size"
              @click="handleDescription"
            >
              {{
                showMore
                  ? $t('CONVERSATION_SIDEBAR.SHOW_LESS')
                  : $t('CONVERSATION_SIDEBAR.SHOW_MORE')
              }}
            </div>
          </div>
          <div
            v-if="!isGroup && hasContactFields"
            class="contact--metadata contact--fields-card"
          >
            <contact-info-row
              :href="contact.email ? `mailto:${contact.email}` : ''"
              :value="contact.email"
              icon="mail"
              emoji="✉️"
              :label="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
              show-copy
            />
            <contact-info-row
              :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
              :value="contact.phone_number"
              :icon="contact.whatsapp_valid ? 'whatsapp' : 'call'"
              emoji="📞"
              :label="$t('CONTACT_PANEL.PHONE_NUMBER')"
              show-copy
            />
            <contact-info-row
              v-if="contact.identifier"
              :value="contact.identifier"
              icon="contact-identify"
              emoji="🪪"
              :label="$t('CONTACT_PANEL.IDENTIFIER')"
            />
            <contact-info-row
              :value="companyNameToShow"
              icon="building-bank"
              emoji="🏢"
              :label="$t('CONTACT_PANEL.COMPANY')"
            />
            <contact-info-row
              :value="location || additionalAttributes.location"
              icon="map"
              emoji="🌍"
              :label="$t('CONTACT_PANEL.LOCATION')"
            />
          </div>
        </div>
        <div v-if="!isTicket" class="contact-actions">
          <woot-button
            v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
            title="$t('CONTACT_PANEL.NEW_MESSAGE')"
            class="new-message bg-primary"
            size="small"
            @click="handleConversations"
          >
            <proto-icon name="chat" />
          </woot-button>
          <woot-button
            v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
            title="$t('EDIT_CONTACT.BUTTON_LABEL')"
            class="edit-contact"
            variant="smooth"
            size="small"
            @click="toggleEditModal"
          >
            <proto-icon name="edit" />
          </woot-button>
          <woot-button
            v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
            title="$t('CONTACT_PANEL.MERGE_CONTACT')"
            class="merge-contact"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          >
            <proto-icon name="merge" />
          </woot-button>
          <woot-button
            v-if="conversationId"
            v-tooltip.top-end="$t('CONTACT_PANEL.SEND_CONTACT')"
            class="send-contact"
            color-scheme="secondary"
            variant="smooth"
            size="small"
            @click="sendContact"
          >
            <proto-icon name="send" />
          </woot-button>

          <woot-button
            v-if="permissionDeleteContact && !isGroup"
            v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
            title="$t('DELETE_CONTACT.BUTTON_LABEL')"
            class="delete-contact"
            variant="smooth"
            size="small"
            color-scheme="alert"
            :disabled="uiFlags.isDeleting"
            @click="toggleDeleteModal"
          >
            <proto-icon name="delete" />
          </woot-button>

          <woot-button
            v-if="permissionCreateTicket"
            v-tooltip="$t('TICKET.CREATE.TITLE')"
            variant="smooth"
            size="small"
            class="new-ticket"
            @click="toggleNewTicketModal"
          >
            <proto-icon name="add" />
          </woot-button>

          <woot-button
            v-if="isGroup"
            v-tooltip="$t('GROUP_PARTICIPANT.TITLE')"
            title="$t('GROUP_PARTICIPANT.TITLE')"
            variant="smooth"
            size="small"
            class="participants-group"
            color-scheme="secondary"
            @click="toggleParticipantsGroups"
          >
            <proto-icon name="people-team" />
          </woot-button>
          <woot-button
            v-if="isGroup"
            v-tooltip="$t('GROUP_PARTICIPANT.ADD_TITLE')"
            title="$t('GROUP_PARTICIPANT.ADD_TITLE')"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            @click="toggleAddNewParticipantGroup"
          >
            <proto-icon name="people-team-add" />
          </woot-button>
        </div>

        <div v-if="isTicket && !hideTicketActions" class="contact-actions">
          <woot-button
            v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
            title="$t('CONTACT_PANEL.NEW_MESSAGE')"
            class="new-message bg-primary"
            icon="chat"
            size="small"
            @click="handleConversations"
          />

          <woot-button
            v-tooltip="$t('CRM_MGMT.FORM.EDIT')"
            class="edit-contact"
            icon="edit"
            variant="smooth"
            size="small"
            @click="toggleUpdateTicketModal"
          />

          <woot-button
            v-tooltip="deleteButtonLabel"
            class="delete-contact"
            icon="delete"
            variant="smooth"
            size="small"
            color-scheme="alert"
            :disabled="uiFlags.isDeleting"
            @click="toggleDeleteTicketModal"
          />

          <woot-button
            v-if="showWinLoseButtons"
            v-tooltip="winTooltip"
            class="win-ticket"
            icon="checkmark"
            variant="smooth"
            size="small"
            color-scheme="success"
            @click="handleWinTicket"
          />

          <woot-button
            v-if="showWinLoseButtons"
            v-tooltip="loseTooltip"
            class="lose-ticket"
            icon="dismiss-circle"
            variant="smooth"
            size="small"
            color-scheme="alert"
            @click="toggleLoseTicketModal"
          />

          <woot-button
            v-if="showReopenButton"
            v-tooltip="reopenTooltip"
            class="reopen-ticket"
            icon="arrow-redo"
            variant="smooth"
            size="small"
            color-scheme="primary"
            @click="handleReopenTicket"
          />
        </div>

        <div v-if="isTicket" class="option-ticket">
          <div class="task-item-body card-select">
            <div v-if="showTaskPriority" class="task-priority-row">
              <div
                class="task-priority"
                :class="[
                  normalizedTicketPriority,
                  { disabled: isAutomaticPriority },
                ]"
                @click="changePriority"
              >
                {{ this.$t('TICKET.FORM.PRIORITY.LABEL_CARD') }}
                {{ priorityTranslate }}
              </div>

              <woot-button
                v-if="isAutomaticPriority && canEditAutomaticPriority"
                v-tooltip="$t('CRM_MGMT.FORM.EDIT')"
                icon="edit"
                variant="clear"
                size="tiny"
                color-scheme="secondary"
                class="priority-edit-answers"
                @click.stop="openPriorityQuestionsModal"
              />
            </div>
            <v-select
              v-if="showTaskPriorityDropdown"
              ref="vueDropdown"
              v-model="ticket.priority"
              :options="
                priorityOptions.map(option => ({
                  label: option.name,
                  value: option.value,
                }))
              "
              :clearable="false"
              :close-on-select="true"
              :reduce="option => option.value"
              class="custom-v-select"
              @search:blur="setNewPriority"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="isTicket">
      <accordion-item
        proto-icon="info"
        :title="$t('TICKET.STATUS.LABEL')"
        :is-open="isContactSidebarItemOpen('is_ticket_status_open')"
        @click="value => toggleSidebarUIState('is_ticket_status_open', value)"
      >
        <div class="status-info">
          <div class="status-row">
            <span class="status-label">{{ $t('TICKET.STATUS.CURRENT') }}:</span>
            <span class="status-badge" :class="statusBadgeClass">{{
              statusTranslate
            }}</span>
          </div>

          <div v-if="ticket.status_reason" class="status-row">
            <span class="status-label">{{ $t('TICKET.STATUS.REASON') }}:</span>
            <p class="status-text">{{ ticket.status_reason }}</p>
          </div>
          <div v-if="ticket.status_comment" class="status-row">
            <span class="status-label">{{ $t('TICKET.STATUS.COMMENT') }}:</span>
            <p class="status-text">{{ ticket.status_comment }}</p>
          </div>
        </div>
      </accordion-item>

      <accordion-item
        proto-icon="tag"
        :title="$t('TICKET.FORM.LABEL.LABEL')"
        :is-open="isContactSidebarItemOpen('is_previous_tickets_open')"
        @click="
          value => toggleSidebarUIState('is_previous_tickets_open', value)
        "
      >
        <woot-button
          v-if="labelsSelected.length < 1"
          icon="add"
          variant="smooth"
          size="small"
          @click="toggleLabelDropdown"
        >
          {{ this.$t('TICKET.FORM.LABEL.ADD') }}
        </woot-button>

        <div>
          <div>
            <multiselect
              v-if="showLabelDropdown"
              v-model="selectedLabels"
              :options="labels"
              track-by="id"
              label="title"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="true"
              placeholder="Pick some"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_REMOVE')"
              @input="setNewLabels"
            />

            <woot-button
              v-if="showLabelDropdown"
              size="tiny"
              color-scheme="primary"
              icon="save"
              @click="saveLabels"
            >
              {{ this.$t('TICKET.FORM.TEXT.SAVE') }}
            </woot-button>
          </div>

          <div class="flex" @click="toggleLabelDropdown">
            <woot-label
              v-for="label in filteredLabels"
              :key="label.id"
              :title="label.title"
              :color="label.color"
              variant="smooth"
              class="cursor-pointer"
              small
            />
          </div>
          <woot-button
            v-if="validateLabels >= 22"
            :title="$t('CONVERSATION.CARD.SHOW_LABELS')"
            class="show-more--button"
            color-scheme="secondary"
            variant="hollow"
            icon="chevron-right"
            size="tiny"
            @click="toggleUpdateTicketModal"
          />
        </div>
      </accordion-item>

      <accordion-item
        proto-icon="people"
        :title="$t('TICKET.FORM.CONTACTS.TITLE')"
        :is-open="isContactSidebarItemOpen('is_ticket_contacts_open')"
        @click="value => toggleSidebarUIState('is_ticket_contacts_open', value)"
      >
        <div
          class="ticket-contact-selector ticket-contact-selector--positioned"
        >
          <div class="ticket-contact-actions">
            <woot-button
              class="ticket-contact-action-btn"
              size="small"
              variant="smooth"
              icon="add"
              @click="toggleContactSelector"
            >
              {{ $t('TICKET.FORM.CONTACTS.ADD') }}
            </woot-button>

            <woot-button
              class="ticket-contact-action-btn"
              size="small"
              variant="smooth"
              icon="add"
              @click="toggleCreateContactModal"
            >
              {{ $t('TICKET.FORM.CONTACTS.CREATE') }}
            </woot-button>
          </div>

          <div
            v-if="associatedContacts.length && hasAnyBulkContactPermission"
            class="ticket-contact-bulk-actions"
          >
            <label class="ticket-contact-bulk-select-all">
              <input
                type="checkbox"
                :checked="allAssociatedContactsSelected"
                @change="toggleSelectAllAssociatedContacts"
              />
              <span>
                {{ selectedAssociatedCount }}
                {{ $t('TICKET.FORM.CONTACTS.TITLE') }}
                {{ $t('BULK_ACTION.CRM.SELECTED_SUFFIX') }}
              </span>
            </label>

            <div
              v-if="selectedAssociatedCount > 0"
              class="ticket-contact-bulk-actions__buttons"
            >
              <woot-button
                v-if="hasBulkMarkersPermission"
                v-tooltip="$t('BULK_ACTION.LABELS.ASSIGN_MARKERS')"
                size="small"
                variant="smooth"
                color-scheme="primary"
                icon="tag"
                @click="toggleAssociatedLabelActions"
              />
              <woot-button
                v-if="hasBulkContactAttributesPermission"
                v-tooltip="$t('BULK_ACTION.CRM.CONTACT_ATTRIBUTES_TOOLTIP')"
                size="small"
                variant="smooth"
                color-scheme="primary"
                icon="settings"
                @click="toggleAssociatedAttributeActions"
              />
            </div>

            <transition name="popover-animation">
              <label-actions
                v-if="showAssociatedLabelActions && hasBulkMarkersPermission"
                class="label-actions-overlay"
                use-contact-labels
                @assign="assignAssociatedLabels"
                @close="showAssociatedLabelActions = false"
              />
            </transition>
            <transition name="popover-animation">
              <label-actions
                v-if="
                  showAssociatedAttributeActions &&
                    hasBulkContactAttributesPermission
                "
                class="label-actions-overlay"
                mode="attributes"
                :ticket-custom-attributes="contactCustomAttributes"
                @assign-custom-attributes="assignAssociatedCustomAttributes"
                @close="showAssociatedAttributeActions = false"
              />
            </transition>
          </div>

          <input
            v-if="showContactSelector"
            v-model.trim="contactSearchQuery"
            class="form-control"
            type="text"
            :placeholder="$t('TICKET.FORM.CONTACTS.SEARCH_PLACEHOLDER')"
            @input="searchContacts"
          />

          <div
            v-if="contactSearchResults.length"
            class="ticket-contact-results"
          >
            <div
              v-for="searchContact in contactSearchResults"
              :key="searchContact.id"
              class="ticket-contact-result-item"
            >
              <div class="ticket-contact-result-content">
                <span>{{ searchContact.name }}</span>
                <small class="ticket-contact-result-meta">
                  {{ contactSearchDisplayInfo(searchContact) }}
                </small>
              </div>
              <woot-button
                size="tiny"
                variant="clear"
                color-scheme="primary"
                icon="add"
                @click="addAssociatedContact(searchContact)"
              />
            </div>
          </div>
        </div>

        <div class="ticket-associated-contacts">
          <div
            v-for="associatedContact in associatedContacts"
            :key="`assoc-${ticket.id}-${associatedContact.id}`"
            class="ticket-associated-item"
          >
            <input
              v-if="hasAnyBulkContactPermission"
              type="checkbox"
              class="ticket-associated-checkbox"
              :checked="isAssociatedContactSelected(associatedContact.id)"
              @change="
                toggleAssociatedContactSelection(
                  associatedContact.id,
                  $event.target.checked
                )
              "
            />
            <span class="ticket-associated-name">{{
              associatedContact.name
            }}</span>
            <div class="ticket-associated-actions">
              <woot-button
                v-tooltip="$t('TICKET.FORM.CONTACTS.SEND_TOOLTIP')"
                :title="$t('TICKET.FORM.CONTACTS.SEND_TOOLTIP')"
                size="tiny"
                variant="clear"
                icon="send"
                color-scheme="secondary"
                @click="openCommunicationModal(associatedContact)"
              />
              <woot-button
                v-tooltip="$t('TICKET.FORM.CONTACTS.INFO_TOOLTIP')"
                :title="$t('TICKET.FORM.CONTACTS.INFO_TOOLTIP')"
                size="tiny"
                variant="clear"
                icon="info"
                color-scheme="secondary"
                @click="openContactInfoModal(associatedContact)"
              />
              <woot-button
                v-tooltip="
                  canRemoveAssociatedContact(associatedContact)
                    ? $t('TICKET.FORM.CONTACTS.REMOVE_TOOLTIP')
                    : $t('TICKET.FORM.CONTACTS.REMOVE_DISABLED_TOOLTIP')
                "
                :title="
                  canRemoveAssociatedContact(associatedContact)
                    ? $t('TICKET.FORM.CONTACTS.REMOVE_TOOLTIP')
                    : $t('TICKET.FORM.CONTACTS.REMOVE_DISABLED_TOOLTIP')
                "
                size="tiny"
                variant="clear"
                icon="dismiss"
                color-scheme="secondary"
                :disabled="!canRemoveAssociatedContact(associatedContact)"
                @click="
                  canRemoveAssociatedContact(associatedContact) &&
                    removeAssociatedContact(associatedContact.id)
                "
              />
            </div>
          </div>
        </div>
      </accordion-item>
    </div>

    <woot-modal
      :show.sync="showBulkContactLabelConfirmModal"
      :on-close="closeBulkContactLabelConfirmModal"
    >
      <div class="bulk-confirm-modal">
        <woot-modal-header
          :header-title="bulkContactLabelModalTitle"
          :header-content="bulkContactLabelModalDescription"
        >
          <p class="bulk-confirm-modal__description bulk-confirm-modal__labels">
            {{ $t('BULK_ACTION.CRM.MARKERS_LIST_LABEL') }}
            <strong>{{ pendingContactLabelAction.labels.join(', ') }}</strong>
          </p>
        </woot-modal-header>
        <div class="modal-footer delete-item bulk-confirm-modal__actions">
          <woot-button
            variant="clear"
            class="action-button btn-primary"
            :disabled="isBulkContactOperationLoading"
            @click="closeBulkContactLabelConfirmModal"
          >
            {{ $t('BULK_ACTION.CRM.BUTTON_KEEP') }}
          </woot-button>
          <woot-button
            :color-scheme="bulkContactLabelConfirmColorScheme"
            variant="smooth"
            class="action-button"
            :is-loading="isBulkContactOperationLoading"
            :disabled="isBulkContactOperationLoading"
            @click="confirmBulkContactLabelAction"
          >
            {{ bulkContactLabelConfirmText }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      :show.sync="showBulkContactAttributeConfirmModal"
      :on-close="closeBulkContactAttributeConfirmModal"
    >
      <div class="bulk-confirm-modal">
        <woot-modal-header
          :header-title="bulkContactAttributeModalTitle"
          :header-content="bulkContactAttributeModalDescription"
        >
          <p class="bulk-confirm-modal__description bulk-confirm-modal__labels">
            {{ $t('BULK_ACTION.CRM.ATTRIBUTE') }}:
            <strong>{{ pendingContactAttributeLabel }}</strong>
          </p>
        </woot-modal-header>
        <div class="modal-footer delete-item bulk-confirm-modal__actions">
          <woot-button
            variant="clear"
            class="action-button btn-primary"
            :disabled="isBulkContactOperationLoading"
            @click="closeBulkContactAttributeConfirmModal"
          >
            {{ $t('BULK_ACTION.CRM.BUTTON_KEEP') }}
          </woot-button>
          <woot-button
            :color-scheme="bulkContactAttributeConfirmColorScheme"
            variant="smooth"
            class="action-button"
            :is-loading="isBulkContactOperationLoading"
            :disabled="isBulkContactOperationLoading"
            @click="confirmBulkContactAttributeAction"
          >
            {{ bulkContactAttributeConfirmText }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <edit-contact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />

    <new-conversation
      v-if="resolvedConversationContact.id"
      :show="showConversationModal"
      :contact-conversations="resolvedConversationConversations"
      :contact="resolvedConversationContact"
      @success="closeConversationModal"
      @cancel="toggleConversationModal"
    />

    <create-contact
      :show="showCreateContactModal"
      @cancel="toggleCreateContactModal"
      @success="handleCreateAssociatedContact"
    />

    <contact-merge-modal
      v-if="showMergeModal"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="toggleMergeModal"
    />

    <woot-delete-modal
      v-if="showDeleteModal"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />

    <woot-delete-modal
      v-if="showDeleteTicketModal"
      :show.sync="showDeleteTicketModal"
      :on-close="closeDeleteTicket"
      :on-confirm="confirmDeletionTicket"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessageText"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />

    <modal-participants-group
      v-if="showGroupParticipants"
      :data="groupParticipants"
      :conversation-info="conversationAttributes"
      @close="toggleParticipantsGroups"
    />

    <modal-add-new-participant
      v-if="showAddNewParticipant"
      :data="groupParticipants"
      :conversation-info="conversationAttributes"
      @close="toggleAddNewParticipantGroup"
    />

    <modal-new-ticket
      v-if="showNewTicket"
      :show="showNewTicket"
      :contact="contact"
      @cancel="toggleNewTicketModal"
    />

    <modal-update-ticket
      v-if="showUpdateTicket"
      :ticket="ticket"
      :show="showUpdateTicket"
      @cancel="toggleUpdateTicketModal"
      @updateTicket="updateTicket"
    />

    <modal-lose-ticket
      v-if="showLoseTicketModal"
      :ticket="ticket"
      :show="showLoseTicketModal"
      @cancel="toggleLoseTicketModal"
      @success="handleLoseSuccess"
    />

    <woot-modal
      v-if="showContactInfoModal"
      :show.sync="showContactInfoModal"
      :on-close="closeContactInfoModal"
      size="modal-sm"
    >
      <div class="column content-box">
        <woot-modal-header
          class="modal-header-centered"
          :header-title="$t('TICKET.FORM.CONTACTS.INFO_TITLE')"
          :header-content="''"
        />

        <div class="ticket-contact-info-content">
          <div class="ticket-contact-info-name-wrap">
            <a
              v-if="selectedContactInfo && selectedContactInfo.id"
              :href="selectedContactProfileLink"
              class="ticket-contact-info-name-link"
              target="_blank"
              rel="noopener nofollow noreferrer"
            >
              {{
                selectedContactInfo && selectedContactInfo.name
                  ? selectedContactInfo.name
                  : '-'
              }}
            </a>
            <span v-else class="ticket-contact-info-name-link">
              {{
                selectedContactInfo && selectedContactInfo.name
                  ? selectedContactInfo.name
                  : '-'
              }}
            </span>

            <a
              v-if="selectedContactInfo && selectedContactInfo.id"
              :href="selectedContactProfileLink"
              class="ticket-contact-info-open-link"
              target="_blank"
              rel="noopener nofollow noreferrer"
            >
              <woot-button
                size="tiny"
                icon="open"
                variant="clear"
                color-scheme="secondary"
              />
            </a>
          </div>

          <div class="ticket-contact-info-rows">
            <contact-info-row
              :href="
                selectedContactInfo && selectedContactInfo.email
                  ? `mailto:${selectedContactInfo.email}`
                  : ''
              "
              :value="
                selectedContactInfo && selectedContactInfo.email
                  ? selectedContactInfo.email
                  : ''
              "
              icon="mail"
              emoji="✉️"
              :label="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
              show-copy
            />

            <contact-info-row
              :href="
                selectedContactInfo && selectedContactInfo.phone_number
                  ? `tel:${selectedContactInfo.phone_number}`
                  : ''
              "
              :value="
                selectedContactInfo && selectedContactInfo.phone_number
                  ? selectedContactInfo.phone_number
                  : ''
              "
              :icon="
                selectedContactInfo && selectedContactInfo.whatsapp_valid
                  ? 'whatsapp'
                  : 'call'
              "
              emoji="📞"
              :label="$t('CONTACT_PANEL.PHONE_NUMBER')"
              show-copy
            />

            <contact-info-row
              v-if="selectedContactInfo && selectedContactInfo.identifier"
              :value="
                selectedContactInfo && selectedContactInfo.identifier
                  ? selectedContactInfo.identifier
                  : ''
              "
              icon="contact-identify"
              emoji="🪪"
              :label="$t('CONTACT_PANEL.IDENTIFIER')"
            />

            <contact-info-row
              v-if="contactCompanyName(selectedContactInfo)"
              :value="contactCompanyName(selectedContactInfo)"
              icon="building-bank"
              emoji="🏢"
              :label="$t('CONTACT_PANEL.COMPANY')"
            />

            <contact-info-row
              v-if="contactLocationDisplay(selectedContactInfo)"
              :value="contactLocationDisplay(selectedContactInfo)"
              icon="map"
              emoji="🌍"
              :label="$t('CONTACT_PANEL.LOCATION')"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <woot-modal
      v-if="showPriorityQuestionsModal"
      :show.sync="showPriorityQuestionsModal"
      :on-close="closePriorityQuestionsModal"
      size="modal-sm"
    >
      <div class="column content-box">
        <woot-modal-header
          class="modal-header-centered"
          :header-title="$t('CRM_MGMT.FORM.EDIT')"
          :header-content="''"
        />

        <div class="priority-answers-modal">
          <label
            v-for="(q, qIdx) in priorityQuestionsForEdit"
            :key="`edit-pq-${qIdx}`"
            class="priority-answer-row"
          >
            {{ q.question }}
            <select v-model.number="priorityAnswersForEdit[qIdx]">
              <option :value="null" disabled>
                {{ $t('TICKET.FORM.URGENCY.SELECT_PLACEHOLDER') }}
              </option>
              <option
                v-for="(opt, optIdx) in q.options"
                :key="`edit-pq-${qIdx}-opt-${optIdx}`"
                :value="opt.weight"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>

          <div class="urgency-summary">
            <span>
              {{ $t('TICKET.FORM.URGENCY.PRIORITY_LABEL') }}:
              {{ calculatedPriorityLabelForEdit }}
            </span>
          </div>

          <div class="modal-footer">
            <div class="medium-12 columns">
              <woot-submit-button
                :disabled="!isEditUrgencyComplete"
                :button-text="$t('TICKET.FORM.TEXT.SAVE')"
                class="bg-primary"
                @click="savePriorityAnswersAndRecalculate"
              />
              <button
                class="button clear"
                @click.prevent="closePriorityQuestionsModal"
              >
                {{ $t('CRM_MGMT.FORM.CANCEL') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
<script>
import { mixin as clickaway } from 'vue-clickaway';
import timeMixin from 'dashboard/mixins/time';
import ContactInfoRow from './ContactInfoRow';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import wootConstants from 'dashboard/constants/globals';
import EditContact from './EditContact';
import NewConversation from './NewConversation';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal';
import alertMixin from 'shared/mixins/alertMixin';
import adminMixin from '../../../../mixins/isAdmin';
import { mapGetters } from 'vuex';
import { getCountryFlag } from 'dashboard/helper/flag';
import ModalParticipantsGroup from './ModalParticipantsGroup.vue';
import ModalAddNewParticipant from './ModalAddNewParticipant.vue';
import ModalNewTicket from './ModalNewTicket.vue';
import ModalUpdateTicket from './ModalUpdateTicket.vue';
import CreateContact from './CreateContact.vue';
import ModalLoseTicket from './ModalLoseTicket.vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import ContactAPI from 'dashboard/api/contacts';
import LabelActions from '../../crm/components/Kanban/LabelActions.vue';
import Spinner from 'shared/components/Spinner';
import ProtoIcon from 'shared/components/ProtoIcon.vue';

export default {
  components: {
    ContactInfoRow,
    EditContact,
    Thumbnail,
    SocialIcons,
    ProtoIcon,
    NewConversation,
    ContactMergeModal,
    ModalParticipantsGroup,
    ModalAddNewParticipant,
    ModalNewTicket,
    vSelect,
    ModalUpdateTicket,
    CreateContact,
    ModalLoseTicket,
    AccordionItem,
    LabelActions,
    Spinner,
  },

  created() {
    const companyId = this.contact && this.contact.company_id;
    if (companyId) {
      this.$store.dispatch('companies/get');
    }
  },
  mixins: [
    alertMixin,
    adminMixin,
    clickaway,
    timeMixin,
    messageFormatterMixin,
    uiSettingsMixin,
  ],
  props: {
    conversation: {
      type: Object,
      default: () => ({}),
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'chevron-right',
    },
    conversationId: {
      type: Number,
      default: undefined,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    message: {
      type: String,
      default: '',
    },
    toggleContact: {
      type: Function,
      default: () => {},
    },
    ticketSelected: {
      type: Object,
      default: () => ({}),
    },
    isTicket: {
      type: Boolean,
      default: false,
    },
    hideTicketActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showEditModal: false,
      showConversationModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showDeleteTicketModal: false,
      showLoseTicketModal: false,
      showMore: false,
      showGroupParticipants: false,
      showAddNewParticipant: false,
      showNewTicket: false,
      acceptChannelTypes: ['Channel::Api'],
      setPage: 1,
      showTaskPriorityDropdown: false,
      showTaskPriority: true,
      showUpdateTicket: false,
      ticket: this.ticketSelected,
      showLabelDropdown: false,
      selectedLabels: [],
      showPriorityQuestionsModal: false,
      priorityAnswersForEdit: [],
      showContactSelector: false,
      showCreateContactModal: false,
      contactSearchQuery: '',
      contactSearchResults: [],
      associatedContacts: [],
      showContactInfoModal: false,
      selectedContactInfo: null,
      conversationContact: null,
      searchContactTimeout: null,
      selectedAssociatedContactIds: [],
      showAssociatedLabelActions: false,
      showAssociatedAttributeActions: false,
      showBulkContactLabelConfirmModal: false,
      pendingContactLabelAction: {
        labels: [],
        actionType: 'add',
      },
      showBulkContactAttributeConfirmModal: false,
      pendingContactAttributeAction: {
        key: '',
        label: '',
        value: '',
        actionType: 'add',
      },
      isBulkContactOperationLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      inboxes: 'inboxes/getInboxes',
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      groupParticipants: 'getGroupParticipants',
      groupParticipantsMeta: 'getGroupParticipantsMeta',
      labels: 'labelsCrm/getLabels',
      kanbanList: 'getKanbanfunnels',
      companies: 'companies/getCompanies',
    }),
    isContactLoading() {
      return this.uiFlags.isFetchingItem && !this.contact.id;
    },
    hasBulkEditPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.crm_settings
      );
    },
    hasBulkContactAttributesPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.bulk_edit_contact_attributes_crm
      );
    },
    hasBulkMarkersPermission() {
      const permissions =
        this.currentUser?.permissions || this.currentUser || {};
      return (
        this.currentUser?.type_role === 'administrator' ||
        !!permissions.bulk_edit_markers_crm
      );
    },
    hasAnyBulkContactPermission() {
      return (
        this.hasBulkEditPermission ||
        this.hasBulkContactAttributesPermission ||
        this.hasBulkMarkersPermission
      );
    },
    contactCustomAttributes() {
      const byModel = this.$store.getters['attributes/getAttributesByModel'];
      return byModel('contact_attribute') || [];
    },
    selectedAssociatedCount() {
      return this.selectedAssociatedContactIds.length;
    },
    allAssociatedContactsSelected() {
      return (
        this.associatedContacts.length > 0 &&
        this.selectedAssociatedCount === this.associatedContacts.length
      );
    },
    pendingContactAttributeLabel() {
      return (
        this.pendingContactAttributeAction.label ||
        this.pendingContactAttributeAction.key
      );
    },
    isBulkContactLabelRemoveAction() {
      return this.pendingContactLabelAction.actionType === 'remove';
    },
    bulkContactLabelModalTitle() {
      return this.isBulkContactLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.CONFIRM_REMOVE_MARKERS_TITLE')
        : this.$t('BULK_ACTION.CRM.CONFIRM_ADD_MARKERS_TITLE');
    },
    bulkContactLabelModalDescription() {
      return this.isBulkContactLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.MARKERS_REMOVE_DESCRIPTION', {
            count: this.selectedAssociatedCount,
            target: this.$t('BULK_ACTION.CRM.CONTACTS_TARGET'),
          })
        : this.$t('BULK_ACTION.CRM.MARKERS_ADD_DESCRIPTION', {
            count: this.selectedAssociatedCount,
            target: this.$t('BULK_ACTION.CRM.CONTACTS_TARGET'),
          });
    },
    bulkContactLabelConfirmColorScheme() {
      return this.isBulkContactLabelRemoveAction ? 'alert' : 'primary';
    },
    bulkContactLabelConfirmText() {
      return this.isBulkContactLabelRemoveAction
        ? this.$t('BULK_ACTION.CRM.BUTTON_REMOVE')
        : this.$t('BULK_ACTION.CRM.BUTTON_ADD');
    },
    isBulkContactAttributeRemoveAction() {
      return this.pendingContactAttributeAction.actionType === 'remove';
    },
    bulkContactAttributeModalTitle() {
      return this.isBulkContactAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.CONFIRM_REMOVE_ATTRIBUTES_TITLE')
        : this.$t('BULK_ACTION.CRM.CONFIRM_UPDATE_ATTRIBUTES_TITLE');
    },
    bulkContactAttributeModalDescription() {
      return this.isBulkContactAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.ATTRIBUTES_REMOVE_DESCRIPTION', {
            count: this.selectedAssociatedCount,
            target: this.$t('BULK_ACTION.CRM.CONTACTS_TARGET'),
          })
        : this.$t('BULK_ACTION.CRM.ATTRIBUTES_UPDATE_DESCRIPTION', {
            count: this.selectedAssociatedCount,
            target: this.$t('BULK_ACTION.CRM.CONTACTS_TARGET'),
          });
    },
    bulkContactAttributeConfirmColorScheme() {
      return this.isBulkContactAttributeRemoveAction ? 'alert' : 'primary';
    },
    bulkContactAttributeConfirmText() {
      return this.isBulkContactAttributeRemoveAction
        ? this.$t('BULK_ACTION.CRM.BUTTON_REMOVE')
        : this.$t('BULK_ACTION.CRM.BUTTON_ADD');
    },

    companyNameToShow() {
      const baseName =
        (this.additionalAttributes && this.additionalAttributes.company_name) ||
        '';
      if (baseName) return baseName;
      const companyId = this.contact && this.contact.company_id;
      if (!companyId) return '';
      const company = (this.companies || []).find(c => c.id === companyId);
      return (company && company.name) || '';
    },

    // Há ao menos um campo do contato preenchido? (esconde o card quando vazio)
    hasContactFields() {
      return !!(
        this.contact.email ||
        this.contact.phone_number ||
        this.contact.identifier ||
        this.companyNameToShow ||
        this.location ||
        (this.additionalAttributes && this.additionalAttributes.location)
      );
    },

    permissionCreateTicket() {
      const account = this.getAccount(this.currentAccountId);
      if (!account?.enable_crm) {
        return false;
      }
      return true;
    },

    labelsSelected() {
      if (!this.isTicket || !this.ticket?.labels_ids.length) return [];
      const labels = this.labels?.filter(label =>
        this.ticket?.labels_ids.includes(label.id)
      );
      this.selectedLabels = labels;
      return labels;
    },

    filteredLabels() {
      let totalCharacters = 0;

      return this.labelsSelected.filter(label => {
        const titleLength = label.title.length;

        if (titleLength >= 22) return false;

        if (totalCharacters + titleLength <= 22) {
          totalCharacters += titleLength;
          return true;
        }

        return false;
      });
    },

    validateLabels() {
      const selectedLabels = this.labelsSelected;
      const totalCharacters = selectedLabels.reduce((acc, label) => {
        return acc + (label.title ? label.title.length : 0);
      }, 0);
      return totalCharacters;
    },

    priorityOptions() {
      return [
        {
          name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL'),
          value: 'critical',
        },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW'), value: 'low' },
        {
          name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM'),
          value: 'medium',
        },
        { name: this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH'), value: 'high' },
      ];
    },

    priorityTranslate() {
      if (!this.isTicket) {
        return null;
      }
      const key = (this.normalizedTicketPriority || '').toUpperCase();
      const i18nKey = `TICKET.FORM.PRIORITY.OPTIONS.${key}`;
      const translated = this.$t(i18nKey);
      if (translated && translated !== i18nKey) return translated;
      const p = this.normalizedTicketPriority;
      if (!p) return '';
      return p.charAt(0).toUpperCase() + p.slice(1);
    },

    normalizedTicketPriority() {
      if (!this.isTicket) return '';
      const raw = (this.ticket?.priority || '').toString().toLowerCase();
      if (!raw) return '';
      if (raw.includes('critical')) return 'critical';
      if (raw.includes('high')) return 'high';
      if (raw.includes('medium')) return 'medium';
      if (raw.includes('low')) return 'low';
      return raw;
    },

    isAutomaticPriority() {
      if (!this.isTicket) return false;
      const kanbanId = this.ticket?.kanbanfunnel_id;
      if (!kanbanId) return false;
      const kanban = (this.kanbanList || []).find(k => k.id === kanbanId);
      return (
        !!kanban?.enable_priority_questions &&
        Array.isArray(kanban?.priority_questions) &&
        kanban.priority_questions.length > 0
      );
    },

    canEditAutomaticPriority() {
      return (
        this.isAutomaticPriority && this.priorityQuestionsForEdit.length > 0
      );
    },

    priorityQuestionsForEdit() {
      if (!this.isAutomaticPriority) return [];
      const kanbanId = this.ticket?.kanbanfunnel_id;
      const kanban = (this.kanbanList || []).find(k => k.id === kanbanId);
      return (kanban?.priority_questions || [])
        .map(q => ({
          question: q?.question || '',
          options: (q?.options || [])
            .map(opt => ({
              label: opt?.label || '',
              weight: Number.isFinite(Number(opt?.weight))
                ? Number(opt.weight)
                : 0,
            }))
            .filter(opt => opt.label),
        }))
        .filter(q => q.question && q.options.length > 0);
    },

    urgencyScoreForEdit() {
      if (!this.canEditAutomaticPriority) return 0;
      return this.priorityAnswersForEdit.reduce((acc, v) => {
        const n = Number(v);
        return acc + (Number.isFinite(n) ? n : 0);
      }, 0);
    },

    calculatedPriorityForEdit() {
      if (!this.canEditAutomaticPriority) return null;
      if (!this.isEditUrgencyComplete) return null;
      if (this.urgencyScoreForEdit >= 100) return 'critical';
      if (this.urgencyScoreForEdit >= 70) return 'high';
      if (this.urgencyScoreForEdit >= 40) return 'medium';
      return 'low';
    },

    calculatedPriorityLabelForEdit() {
      const p = this.calculatedPriorityForEdit;
      if (!p) return '-';
      return this.$t(`TICKET.FORM.PRIORITY.OPTIONS.${p.toUpperCase()}`);
    },

    isEditUrgencyComplete() {
      if (!this.canEditAutomaticPriority) return false;
      return this.priorityQuestionsForEdit.every((_q, idx) => {
        const v = this.priorityAnswersForEdit?.[idx];
        return (
          v !== null &&
          v !== undefined &&
          v !== '' &&
          v !== 'null' &&
          !Number.isNaN(v)
        );
      });
    },

    className() {
      return this.showMore ? 'description-complete' : 'description';
    },
    isLong() {
      return this.conversation?.group?.description.length > 450;
    },
    descriptionFormatter() {
      if (this.conversation?.group?.description) {
        const formattedText = this.whatsappFormatter(
          this.conversation?.group?.description
        );
        return this.formatMessage(formattedText);
      }
      return '';
    },
    isGroup() {
      if (this?.conversation?.group) {
        return true;
      }
      return false;
    },
    isOnline() {
      return this.contact.availability_status === 'online';
    },
    hasDescription() {
      if (this?.conversation?.group?.description) {
        return true;
      }
      return false;
    },
    conversationAttributes() {
      return this.conversation;
    },
    contactConversations() {
      const currentId = this.currentUser?.id;
      const currentConversations = this.$store.getters[
        'contactConversations/getContactConversation'
      ](
        this.contact?.id,
        currentId,
        this.isLimitedAgent,
        !this.isPermissionSeeAllTeamConversation
      );
      return currentConversations;
    },
    resolvedConversationContact() {
      return this.conversationContact || this.contact;
    },
    resolvedConversationConversations() {
      const currentId = this.currentUser?.id;
      return this.$store.getters['contactConversations/getContactConversation'](
        this.resolvedConversationContact?.id,
        currentId,
        this.isLimitedAgent,
        !this.isPermissionSeeAllTeamConversation
      );
    },
    inbox() {
      if (this.inboxes.length === 1) return this.inboxes[0];

      return false;
    },
    currentInboxId() {
      if (this.inbox) return this.inbox.id;

      return this.$route.params.inboxId;
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    selectedContactProfileLink() {
      if (!this.selectedContactInfo || !this.selectedContactInfo.id) return '';
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.selectedContactInfo.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    openConversations() {
      if (this.currentInboxId) {
        return this.contactConversations.filter(
          conversation =>
            conversation.inbox_id === this.currentInboxId &&
            conversation.status !== 'resolved' &&
            conversation?.meta?.channel !== 'Channel::Internal'
        );
      }

      return this.contactConversations.filter(
        conversation => conversation.status !== 'resolved'
      );
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
      } = this.additionalAttributes;

      return { twitter: twitterScreenName, ...(socialProfiles || {}) };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },

    confirmDeleteMessageText() {
      return `#${this.ticket.id} ${this.ticket.text}?`;
    },

    permissionDeleteContact() {
      return this.currentUser.permissions.delete_contact;
    },

    showWinLoseButtons() {
      if (!this.isTicket || !this.ticket?.kanbanfunnel_id) {
        return false;
      }
      const kanban = this.kanbanList.find(
        k => k.id === this.ticket.kanbanfunnel_id
      );
      const isBusinessOpportunity =
        kanban &&
        (kanban.type_opportunity === 'business' ||
          kanban.type_opportunity === 'both');
      const ticketStatus = this.ticket?.status || 'open';
      // Não mostrar botões se já está ganho ou perdido
      const isNotFinal = ticketStatus !== 'gain' && ticketStatus !== 'lose';
      return isBusinessOpportunity && isNotFinal;
    },

    showReopenButton() {
      if (!this.isTicket || !this.ticket?.kanbanfunnel_id) {
        return false;
      }
      const kanban = this.kanbanList.find(
        k => k.id === this.ticket.kanbanfunnel_id
      );
      const ticketStatus = this.ticket?.status || 'open';
      // Mostrar botão de reabrir apenas se não está aberto
      return ticketStatus !== 'open';
    },

    statusTranslate() {
      if (!this.isTicket || !this.ticket?.status) return '';
      const statusMap = {
        open: this.$t('TICKET.STATUS.OPEN'),
        gain: this.$t('TICKET.STATUS.GAIN'),
        lose: this.$t('TICKET.STATUS.LOSE'),
        deleted: this.$t('TICKET.STATUS.DELETED'),
      };
      return statusMap[this.ticket.status] || this.ticket.status;
    },

    statusBadgeClass() {
      const status = this.ticket?.status || 'open';
      return `status-${status}`;
    },

    deleteButtonLabel() {
      if (!this.isTicket || !this.ticket?.kanbanfunnel_id) {
        return this.$t('TICKET.DELETE.LABEL');
      }
      const kanban = this.kanbanList.find(
        k => k.id === this.ticket.kanbanfunnel_id
      );
      const isBusinessOpportunity =
        kanban &&
        (kanban.type_opportunity === 'business' ||
          kanban.type_opportunity === 'both');
      return isBusinessOpportunity
        ? this.$t('TICKET.DELETE.BUSINESS_LABEL')
        : this.$t('TICKET.DELETE.LABEL');
    },

    isBusinessOpportunityTicket() {
      if (!this.isTicket || !this.ticket?.kanbanfunnel_id) {
        return false;
      }
      const kanban = this.kanbanList.find(
        k => k.id === this.ticket.kanbanfunnel_id
      );
      return (
        kanban &&
        (kanban.type_opportunity === 'business' ||
          kanban.type_opportunity === 'both')
      );
    },

    winTooltip() {
      return this.isBusinessOpportunityTicket
        ? this.$t('TICKET.WIN.BUSINESS_TOOLTIP')
        : this.$t('TICKET.WIN.TOOLTIP');
    },

    loseTooltip() {
      return this.isBusinessOpportunityTicket
        ? this.$t('TICKET.LOSE.BUSINESS_TOOLTIP')
        : this.$t('TICKET.LOSE.TOOLTIP');
    },

    reopenTooltip() {
      return this.isBusinessOpportunityTicket
        ? this.$t('TICKET.REOPEN.BUSINESS_TOOLTIP')
        : this.$t('TICKET.REOPEN.TOOLTIP');
    },
  },

  watch: {
    $route: {
      handler() {
        this.fetchDataGroup();
      },
    },
    ticketSelected: {
      handler(newTicket) {
        this.ticket = newTicket || {};
        this.clearSelectedAssociatedContacts();
        this.initializeAssociatedContacts();
      },
      deep: true,
      immediate: true,
    },
    associatedContacts() {
      const validIds = new Set(
        this.associatedContacts.map(contact => contact.id)
      );
      this.selectedAssociatedContactIds = this.selectedAssociatedContactIds.filter(
        contactId => validIds.has(contactId)
      );
    },
  },
  beforeMount() {
    this.fetchData();
    this.fetchDataGroup();
    this.initializeAssociatedContacts();
  },
  methods: {
    isAssociatedContactSelected(contactId) {
      return this.selectedAssociatedContactIds.includes(contactId);
    },

    clearSelectedAssociatedContacts() {
      this.selectedAssociatedContactIds = [];
      this.showAssociatedLabelActions = false;
      this.showAssociatedAttributeActions = false;
    },

    toggleAssociatedContactSelection(contactId, selected) {
      const selectedSet = new Set(this.selectedAssociatedContactIds);
      if (selected) {
        selectedSet.add(contactId);
      } else {
        selectedSet.delete(contactId);
      }
      this.selectedAssociatedContactIds = Array.from(selectedSet);
    },

    toggleSelectAllAssociatedContacts(event) {
      if (!event.target.checked) {
        this.clearSelectedAssociatedContacts();
        return;
      }

      this.selectedAssociatedContactIds = this.associatedContacts.map(
        contact => contact.id
      );
    },

    toggleAssociatedLabelActions() {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }
      this.showAssociatedAttributeActions = false;
      this.showAssociatedLabelActions = !this.showAssociatedLabelActions;
    },

    async toggleAssociatedAttributeActions() {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!this.contactCustomAttributes.length) {
        await this.$store.dispatch('attributes/get');
      }

      this.showAssociatedLabelActions = false;
      this.showAssociatedAttributeActions = !this
        .showAssociatedAttributeActions;
    },

    assignAssociatedLabels({ labels, actionType }) {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!labels?.length || this.selectedAssociatedCount === 0) {
        return;
      }

      this.pendingContactLabelAction = {
        labels,
        actionType: actionType || 'add',
      };
      this.showBulkContactLabelConfirmModal = true;
    },

    closeBulkContactLabelConfirmModal() {
      if (this.isBulkContactOperationLoading) {
        return;
      }

      this.showBulkContactLabelConfirmModal = false;
      this.pendingContactLabelAction = {
        labels: [],
        actionType: 'add',
      };
    },

    assignAssociatedCustomAttributes({ key, value, actionType }) {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        return;
      }

      if (!key || this.selectedAssociatedCount === 0) {
        this.showAlert(this.$t('BULK_ACTION.CRM.ENTER_ATTRIBUTE_KEY'));
        return;
      }

      if (actionType === 'add' && value === '') {
        this.showAlert(this.$t('BULK_ACTION.CRM.ENTER_ATTRIBUTE_VALUE'));
        return;
      }

      const selectedAttribute = this.contactCustomAttributes.find(
        attribute => attribute.attribute_key === key
      );

      this.pendingContactAttributeAction = {
        key,
        label: selectedAttribute?.attribute_display_name || key,
        value,
        actionType: actionType || 'add',
      };

      this.showBulkContactAttributeConfirmModal = true;
    },

    closeBulkContactAttributeConfirmModal() {
      if (this.isBulkContactOperationLoading) {
        return;
      }

      this.showBulkContactAttributeConfirmModal = false;
      this.pendingContactAttributeAction = {
        key: '',
        label: '',
        value: '',
        actionType: 'add',
      };
    },

    async confirmBulkContactLabelAction() {
      if (!this.hasBulkMarkersPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        this.closeBulkContactLabelConfirmModal();
        return;
      }

      if (
        !this.pendingContactLabelAction.labels.length ||
        this.selectedAssociatedCount === 0
      ) {
        this.closeBulkContactLabelConfirmModal();
        return;
      }

      this.isBulkContactOperationLoading = true;

      try {
        const labelsToChange = this.pendingContactLabelAction.labels;
        const labelsSet = new Set(labelsToChange);

        for (const contactId of this.selectedAssociatedContactIds) {
          await this.$store.dispatch('contactLabels/get', contactId);
          const currentLabels =
            this.$store.getters['contactLabels/getContactLabels'](contactId) ||
            [];

          const updatedLabels =
            this.pendingContactLabelAction.actionType === 'remove'
              ? currentLabels.filter(label => !labelsSet.has(label))
              : Array.from(new Set([...currentLabels, ...labelsToChange]));

          await this.$store.dispatch('contactLabels/update', {
            contactId,
            labels: updatedLabels,
          });
        }

        this.showAlert(
          this.pendingContactLabelAction.actionType === 'remove'
            ? this.$t('BULK_ACTION.CRM.LABELS_REMOVED_SUCCESS')
            : this.$t('BULK_ACTION.CRM.LABELS_ASSIGNED_SUCCESS')
        );
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('BULK_ACTION.CRM.LABELS_UPDATE_FAILED')
        );
      } finally {
        this.isBulkContactOperationLoading = false;
        this.closeBulkContactLabelConfirmModal();
        this.showAssociatedLabelActions = false;
        this.clearSelectedAssociatedContacts();
      }
    },

    async confirmBulkContactAttributeAction() {
      if (!this.hasBulkContactAttributesPermission) {
        this.showAlert(this.$t('BULK_ACTION.CRM.PERMISSION_DENIED'));
        this.closeBulkContactAttributeConfirmModal();
        return;
      }

      if (
        !this.pendingContactAttributeAction.key ||
        this.selectedAssociatedCount === 0
      ) {
        this.closeBulkContactAttributeConfirmModal();
        return;
      }

      this.isBulkContactOperationLoading = true;

      try {
        for (const contactId of this.selectedAssociatedContactIds) {
          if (this.pendingContactAttributeAction.actionType === 'remove') {
            await this.$store.dispatch('contacts/deleteCustomAttributes', {
              id: contactId,
              customAttributes: [this.pendingContactAttributeAction.key],
            });
          } else {
            await this.$store.dispatch('contacts/update', {
              id: contactId,
              custom_attributes: {
                [this.pendingContactAttributeAction.key]: this
                  .pendingContactAttributeAction.value,
              },
            });
          }
        }

        this.showAlert(
          this.pendingContactAttributeAction.actionType === 'remove'
            ? this.$t('BULK_ACTION.CRM.ATTRIBUTE_REMOVED_SUCCESS')
            : this.$t('BULK_ACTION.CRM.ATTRIBUTE_UPDATED_SUCCESS')
        );
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('BULK_ACTION.CRM.ATTRIBUTE_UPDATE_FAILED')
        );
      } finally {
        this.isBulkContactOperationLoading = false;
        this.closeBulkContactAttributeConfirmModal();
        this.showAssociatedAttributeActions = false;
        this.clearSelectedAssociatedContacts();
        await this.initializeAssociatedContacts();
      }
    },

    setNewLabels(newLabels) {
      this.selectedLabels = newLabels;
    },

    toggleLabelDropdown() {
      this.showLabelDropdown = !this.showLabelDropdown;
    },

    async saveLabels() {
      const labelIds = this.selectedLabels.map(label => label.id);
      this.ticket.labels_ids = labelIds;
      await this.$store.dispatch('updateTicket', this.ticket);
      this.toggleLabelDropdown();
    },

    async initializeAssociatedContacts() {
      if (!this.isTicket || !this.ticket?.id) {
        this.associatedContacts = [];
        return;
      }

      try {
        const response = await this.$store.dispatch('getTicketContacts', {
          ticketId: this.ticket.id,
        });
        this.associatedContacts = this.normalizeTicketContactsResponse(
          response
        );
      } catch (_error) {
        this.associatedContacts = [];
      }
    },

    normalizeTicketContactsResponse(response) {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.payload)) return response.payload;
      if (Array.isArray(response?.data)) return response.data;
      return [];
    },

    extractCreatedContact(createdContact) {
      if (!createdContact) return null;
      if (createdContact?.id) return createdContact;
      if (createdContact?.payload?.contact?.id)
        return createdContact.payload.contact;
      if (createdContact?.payload?.id) return createdContact.payload;
      if (createdContact?.contact?.id) return createdContact.contact;
      if (createdContact?.data?.payload?.contact?.id)
        return createdContact.data.payload.contact;
      return null;
    },

    toggleContactSelector() {
      this.showContactSelector = !this.showContactSelector;
      if (!this.showContactSelector) {
        this.contactSearchQuery = '';
        this.contactSearchResults = [];
      }
    },

    toggleCreateContactModal() {
      this.showCreateContactModal = !this.showCreateContactModal;
    },

    contactSearchDisplayInfo(contact) {
      const info = [contact?.email, contact?.phone_number].filter(Boolean);
      return info.length ? info.join(' • ') : `ID: ${contact?.id}`;
    },

    contactLocationDisplay(contactItem) {
      if (!contactItem) return '';
      const attrs = contactItem.additional_attributes || {};
      const country = attrs.country || '';
      const city = attrs.city || '';
      const countryCode = attrs.country_code;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');
      if (!cityAndCountry) return '';
      return this.findCountryFlag(countryCode, cityAndCountry);
    },

    contactCompanyName(contactItem) {
      if (!contactItem) return '';
      return contactItem?.additional_attributes?.company_name || '';
    },

    searchContacts() {
      if (this.searchContactTimeout) {
        clearTimeout(this.searchContactTimeout);
      }

      this.searchContactTimeout = setTimeout(async () => {
        if (!this.contactSearchQuery || this.contactSearchQuery.length < 2) {
          this.contactSearchResults = [];
          return;
        }

        try {
          const response = await ContactAPI.search(
            this.contactSearchQuery,
            1,
            'name',
            '',
            true
          );
          const payload = response?.data?.payload || [];
          this.contactSearchResults = payload.filter(
            contact =>
              !this.associatedContacts.find(item => item.id === contact.id)
          );
        } catch (_error) {
          this.contactSearchResults = [];
        }
      }, 300);
    },

    async addAssociatedContact(contact) {
      if (!this.ticket?.id || !contact?.id) return false;

      try {
        const response = await this.$store.dispatch('addTicketContact', {
          ticketId: this.ticket.id,
          contactId: contact.id,
        });
        this.associatedContacts = this.normalizeTicketContactsResponse(
          response
        );
        this.clearSelectedAssociatedContacts();
        this.$emit('ticket-contacts-updated', this.ticket);
        return true;
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('TICKET.FORM.COMMUNICATIONS.SEND_ERROR')
        );
        return false;
      } finally {
        this.contactSearchQuery = '';
        this.contactSearchResults = [];
      }
    },

    async handleCreateAssociatedContact(createdContact) {
      const normalizedContact = this.extractCreatedContact(createdContact);
      if (!normalizedContact?.id) {
        await this.initializeAssociatedContacts();
        return;
      }

      const associationSucceeded = await this.addAssociatedContact(
        normalizedContact
      );
      if (!associationSucceeded) return;

      if (
        !this.associatedContacts.find(item => item.id === normalizedContact.id)
      ) {
        await this.initializeAssociatedContacts();
      }

      if (
        this.associatedContacts.find(item => item.id === normalizedContact.id)
      ) {
        this.showAlert(this.$t('TICKET.FORM.CONTACTS.CREATE_AND_LINK_SUCCESS'));
      }
    },

    async removeAssociatedContact(contactId) {
      if (!this.ticket?.id || !contactId) return;

      try {
        const response = await this.$store.dispatch('removeTicketContact', {
          ticketId: this.ticket.id,
          contactId,
        });
        this.associatedContacts = this.normalizeTicketContactsResponse(
          response
        );
        this.clearSelectedAssociatedContacts();
        this.$emit('ticket-contacts-updated', this.ticket);
      } catch (error) {
        this.showAlert(
          error?.message || this.$t('TICKET.FORM.COMMUNICATIONS.SEND_ERROR')
        );
      }
    },

    canRemoveAssociatedContact(contact) {
      const primaryContactId = this.ticket?.contact_id || this.contact?.id;
      return contact?.id !== primaryContactId;
    },

    openContactInfoModal(contact) {
      this.selectedContactInfo = contact;
      this.showContactInfoModal = true;
    },

    closeContactInfoModal() {
      this.showContactInfoModal = false;
      this.selectedContactInfo = null;
    },

    openCommunicationModal(contact = null) {
      this.conversationContact = contact || this.contact;
      this.showConversationModal = true;
    },

    toggleUpdateTicketModal() {
      this.showUpdateTicket = !this.showUpdateTicket;
    },
    updateTicket(updatedTicket) {
      this.ticket = { ...this.ticket, ...updatedTicket };
      this.$emit('updateTicket', this.ticket);
    },
    changePriority() {
      if (this.isAutomaticPriority) return;
      this.showTaskPriorityDropdown = !this.showTaskPriorityDropdown;
      this.showTaskPriority = !this.showTaskPriority;
      this.$nextTick(() => {
        const input = this.$refs.vueDropdown.$el.querySelector('input');
        input.focus();
      });
    },
    async setNewPriority() {
      if (this.isAutomaticPriority) return;
      this.showTaskPriorityDropdown = !this.showTaskPriorityDropdown;
      this.showTaskPriority = !this.showTaskPriority;
      await this.$store.dispatch('updateTicket', this.ticket);
    },

    openPriorityQuestionsModal() {
      if (!this.canEditAutomaticPriority) return;
      const fromArray = Array.isArray(this.ticket?.urgency_answers)
        ? this.ticket.urgency_answers
        : null;

      const fromLegacy = [
        this.ticket?.urgency_q1,
        this.ticket?.urgency_q2,
        this.ticket?.urgency_q3,
        this.ticket?.urgency_q4,
        this.ticket?.urgency_q5,
      ];

      const defaultsSource = fromArray || fromLegacy;
      const defaults = (defaultsSource || []).map(v =>
        v === null || v === undefined || v === '' ? null : Number(v)
      );

      this.priorityAnswersForEdit = Array.from(
        { length: this.priorityQuestionsForEdit.length },
        (_v, idx) => (idx < defaults.length ? defaults[idx] : null)
      );
      this.showPriorityQuestionsModal = true;
    },

    closePriorityQuestionsModal() {
      this.showPriorityQuestionsModal = false;
    },

    async savePriorityAnswersAndRecalculate() {
      if (!this.isEditUrgencyComplete) return;

      const payload = { id: this.ticket.id };
      const answers = (this.priorityAnswersForEdit || []).map(v =>
        v === null || v === undefined || v === '' ? null : Number(v)
      );

      payload.urgency_answers = answers;

      const updated = await this.$store.dispatch('updateTicket', payload);
      this.ticket = { ...this.ticket, ...updated };
      this.$emit('updateTicket', this.ticket);
      this.showPriorityQuestionsModal = false;
    },

    async fetchData() {
      await this.$store.dispatch('fetchKanbanfunnels');
    },

    async fetchDataGroup() {
      if (this.conversation?.group) {
        const group = this.conversation?.group;
        const payload = {
          groupId: group.id,
          accountId: group.account_id,
          page: 1,
        };
        await this.$store.dispatch('fetchGroupParticipants', payload);
      }
    },

    whatsappFormatter(text) {
      const {
        ITALIC_PATTERN,
        ITALIC_CHARACTER,
        BOLD_PATTERN,
        BOLD_CHARACTER,
        ITALIC_BOLD_PATTERN,
        ITALIC_BOLD_CHARACTER,
        ITALIC_BOLD_CHARACTER_REVERSE,
      } = wootConstants.WHATSAPP_FORMATTER_REVERSE;
      text = text.replace(
        ITALIC_BOLD_PATTERN,
        `${ITALIC_BOLD_CHARACTER}$1${ITALIC_BOLD_CHARACTER_REVERSE}`
      );
      text = text.replace(BOLD_PATTERN, `${BOLD_CHARACTER}$1${BOLD_CHARACTER}`);
      text = text.replace(
        ITALIC_PATTERN,
        `${ITALIC_CHARACTER}$1${ITALIC_CHARACTER}`
      );
      return text;
    },
    closeConversationModal() {
      this.toggleConversationModal();

      this.$emit('toggle-contacts-view');
    },
    toggleAddNewParticipantGroup() {
      this.showAddNewParticipant = !this.showAddNewParticipant;
    },
    toggleParticipantsGroups() {
      this.showGroupParticipants = !this.showGroupParticipants;
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    toggleNewTicketModal() {
      this.showNewTicket = !this.showNewTicket;
    },
    onPanelToggle() {
      this.$emit('toggle-panel');
    },
    isSingleNonOfficialWhatsappInbox() {
      return (
        this.inbox &&
        this.inbox?.is_whatsapp &&
        this.acceptChannelTypes.includes(this.inbox?.channel_type)
      );
    },
    handleDescription() {
      this.showMore = !this.showMore;
    },
    handleConversations() {
      this.conversationContact = null;
      if (this.isSingleNonOfficialWhatsappInbox()) {
        switch (this.openConversations.length) {
          case 0: {
            return this.createConversation({
              inboxId: this.inbox.id,
              contactId: this.contact.id,
              assigneeId: this.currentUser.id,
            });
          }
          default: {
            return this.showAlert(
              this.$t('CONTACT_PANEL.ALREADY_OPEN_CONVERSATIONS')
            );
          }
        }
      } else {
        this.toggleConversationModal();
        return undefined;
      }
    },
    toggleConversationModal() {
      if (this.showConversationModal) {
        this.conversationContact = null;
      }
      this.showConversationModal = !this.showConversationModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },

    toggleDeleteTicketModal() {
      this.showDeleteTicketModal = !this.showDeleteTicketModal;
    },

    toggleLoseTicketModal() {
      this.showLoseTicketModal = !this.showLoseTicketModal;
    },

    async handleWinTicket() {
      try {
        const payload = {
          ...this.ticket,
          status: 'gain',
        };
        await this.$store.dispatch('updateTicket', payload);
        const message = this.isBusinessOpportunityTicket
          ? this.$t('TICKET.WIN.BUSINESS_SUCCESS')
          : this.$t('TICKET.WIN.SUCCESS');
        this.showAlert(message);
        this.$emit('close-modal-ticket', true);
      } catch (error) {
        const message = this.isBusinessOpportunityTicket
          ? this.$t('TICKET.WIN.BUSINESS_ERROR')
          : this.$t('TICKET.WIN.ERROR');
        this.showAlert(message);
      }
    },

    async handleReopenTicket() {
      try {
        const payload = {
          ...this.ticket,
          status: 'open',
          status_reason: null,
          status_comment: null,
        };
        await this.$store.dispatch('updateTicket', payload);
        const message = this.isBusinessOpportunityTicket
          ? this.$t('TICKET.REOPEN.BUSINESS_SUCCESS')
          : this.$t('TICKET.REOPEN.SUCCESS');
        this.showAlert(message);
        this.$emit('close-modal-ticket', true);
      } catch (error) {
        const message = this.isBusinessOpportunityTicket
          ? this.$t('TICKET.REOPEN.BUSINESS_ERROR')
          : this.$t('TICKET.REOPEN.ERROR');
        this.showAlert(message);
      }
    },

    handleLoseSuccess() {
      this.toggleLoseTicketModal();
      this.$emit('close-modal-ticket', true);
    },

    toggleContactsModal() {
      this.$emit('toggle-contacts-view');
    },
    sendContact() {
      this.toggleContact(this.contact);
      this.onSend();
      this.$emit('toggle-contacts-view');
      this.closeDelete();
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    confirmDeletionTicket() {
      const payload = {
        ...this.ticket,
        status: 'deleted',
      };

      this.$store
        .dispatch('updateTicket', payload)
        .then(() => {
          const message = this.isBusinessOpportunityTicket
            ? this.$t('TICKET.DELETE.API.BUSINESS_SUCCESS_MESSAGE')
            : this.$t('TICKET.DELETE.API.SUCCESS_MESSAGE');
          this.showAlert(message);
          this.$emit('close-modal-ticket', true);
        })
        .catch(error => {
          const errorMessage = this.isBusinessOpportunityTicket
            ? error?.message ||
              this.$t('TICKET.DELETE.API.BUSINESS_ERROR_MESSAGE')
            : error?.message || this.$t('TICKET.DELETE.API.ERROR_MESSAGE');
          this.showAlert(errorMessage);
        });
    },

    closeDeleteTicket() {
      this.showDeleteTicketModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },

    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        const countryFlag = countryCode ? getCountryFlag(countryCode) : '🌎';
        return `${cityAndCountry} ${countryFlag}`;
      } catch (error) {
        return '';
      }
    },
    async createConversation(payload) {
      try {
        const data = await this.onSubmit(payload);
        this.toggleContactsModal();

        this.$router.push(
          `/app/accounts/${data.account_id}/conversations/${data.id}`
        );
      } catch (error) {
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },
    async onSubmit(contactItem) {
      const data = await this.$store.dispatch(
        'contactConversations/create',
        contactItem
      );
      return data;
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panel-close');
        this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));
        if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({ name: 'contacts_dashboard' });
        }
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.toggleMergeModal();
    },
  },
};
</script>

<style scoped lang="scss">
.option-ticket {
  margin-top: 2rem;
}

.task-priority-row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.priority-edit-answers {
  padding: 0;
}

.priority-answers-modal {
  padding: var(--space-normal);
}

.ticket-contact-info-content {
  padding: 0 var(--space-normal) var(--space-normal);
  background: var(--s-25);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius);
}

.ticket-contact-info-name-wrap {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-small);
  padding: var(--space-small) var(--space-small) var(--space-smaller);
}

.ticket-contact-info-name-link {
  color: var(--b-500);
  font-size: var(--font-size-default);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  word-break: break-word;
}

.ticket-contact-info-name-link:hover {
  text-decoration: underline;
}

.ticket-contact-info-open-link {
  margin-left: var(--space-smaller);
}

.ticket-contact-info-rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  padding: var(--space-small);
  border-top: 1px solid var(--s-100);
}

.priority-answer-row {
  display: block;
  margin-bottom: var(--space-small);
}

.task-priority {
  display: inline;
  color: #fff;
  font-size: 13px;
  padding: 0.3rem 0.5rem;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  box-shadow: 0px 0px 5px -2px rgba(134, 134, 162, 1);
  background: linear-gradient(89deg, #ff5b5b 0, #ff8b5b 100%);
}

.task-priority.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.task-priority.low {
  background: linear-gradient(89deg, #66a7f8 0, #8ea7fb 100%);
}

.task-priority.medium {
  background: linear-gradient(89deg, #79d3b9 0, #81d5de 100%);
}

.task-priority.high {
  background: linear-gradient(89deg, #cd74d1 0, #db78a3 100%);
}

.task-priority.critical {
  background: linear-gradient(89deg, #ff5b5b 0, #ff8b5b 100%);
}

.card-select {
  width: 70%;
}

.description {
  max-height: 29vh;
  overflow-y: hidden;
}

.description-complete {
  max-height: 100vh;
}

.description-size {
  color: #07c4dc;
  cursor: pointer;
  width: fit-content;
}

.description-size:hover {
  text-decoration: underline;
}

.contact--profile {
  position: relative;
  align-items: center;
  padding: var(--space-two) var(--space-normal) var(--space-normal);
  text-align: center;
}

.close-button--rtl {
  position: absolute;
  top: var(--space-small);
  right: var(--space-small);
}

.contact--profile-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-small);
  color: var(--b-500);
}

.contact--details {
  margin-top: var(--space-small);
  width: 100%;
  text-align: center;
}

.contact--info {
  text-align: center;
}

.contact-info--header {
  display: flex;
  justify-content: center;
  flex-direction: row;
}

.contact--name-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-micro);
  margin-bottom: var(--space-smaller);
}

.contact--name {
  text-transform: capitalize;
  white-space: normal;
  margin: 0 var(--space-smaller) 0 var(--space-smaller);
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  // .sub-block-title base usa `color: var(--g-20) !important`, então precisamos de !important
  color: #002136 !important;

  a {
    color: var(--color-body);
  }
}

.contact--status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--s-500);
  margin-bottom: var(--space-small);

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--s-400);
  }

  &.is-online {
    color: #1f9d63;

    .status-dot {
      background: #1f9d63;
    }
  }
}

.contact--metadata {
  margin-top: var(--space-normal);
  margin-bottom: var(--space-slab);
  text-align: left;
}

// Card que agrupa os campos do contato (evita que fiquem "soltos")
.contact--fields-card {
  background: var(--s-25);
  border: 1px solid var(--s-50);
  border-radius: 10px;
  padding: var(--space-micro) var(--space-normal);
}

.contact-actions {
  margin-top: var(--space-normal);
}

.contact-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: var(--space-small);

  .new-message,
  .edit-contact,
  .merge-contact,
  .send-contact,
  .participants-group,
  .new-ticket,
  .delete-contact,
  .win-ticket,
  .lose-ticket,
  .reopen-ticket {
    margin-right: 0;
  }
}

.contact-actions ::v-deep .button {
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  border-radius: 10px;
  align-items: center;
  justify-content: center;

  .button__content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 0;
  }

  .icon,
  i {
    margin: 0;
  }
}

.status-info {
  padding: var(--space-small) 0;
}

.status-row {
  margin-bottom: var(--space-small);
  &:last-child {
    margin-bottom: 0;
  }
}

.status-label {
  font-weight: var(--font-weight-medium);
  color: var(--s-700);
  font-size: var(--font-size-small);
  display: block;
  margin-bottom: var(--space-micro);
}

.status-text {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin: 0;
  word-wrap: break-word;
}

.status-badge {
  display: inline-block;
  padding: var(--space-micro) var(--space-small);
  border-radius: var(--border-radius-small);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  &.status-open {
    background-color: var(--b-100);
    color: var(--b-700);
  }
  &.status-gain {
    background-color: var(--g-100);
    color: var(--g-700);
  }
  &.status-lose {
    background-color: var(--r-100);
    color: var(--r-700);
  }
  &.status-deleted {
    background-color: var(--s-100);
    color: var(--s-700);
  }
}

.merge-summary--card {
  padding: var(--space-normal);
}

.contact--bio {
  word-wrap: break-word;
}

.button--contact-menu {
  position: absolute;
  right: var(--space-normal);
  top: 0;
}

.ticket-contact-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius);
  background: var(--white);
}

.ticket-contact-result-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  span {
    color: var(--s-900);
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.ticket-contact-result-meta {
  color: var(--s-600);
  font-size: var(--font-size-mini);
}

.ticket-contact-selector {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.ticket-contact-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--space-smaller);
}

.ticket-contact-action-btn {
  flex: 1 1 0;
  min-width: 0;
  width: 100%;

  ::v-deep .button,
  ::v-deep button {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
  }

  ::v-deep .button__content {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: var(--space-micro);
    margin: 0 auto !important;
  }

  ::v-deep .button__icon,
  ::v-deep .fluent-icon {
    margin: 0 !important;
  }
}

.ticket-contact-bulk-actions {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius);
  display: flex;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small);
}

.ticket-contact-bulk-select-all {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  gap: var(--space-small);
  margin: 0;
}

.ticket-contact-bulk-select-all input[type='checkbox'] {
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
}

.ticket-contact-bulk-select-all span {
  color: var(--s-900);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
}

.ticket-contact-bulk-actions__buttons {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
}

.ticket-contact-bulk-actions__trigger {
  position: relative;
}

.ticket-contact-selector--positioned {
  position: relative;

  ::v-deep .label-actions-overlay {
    left: 0 !important;
    right: 0 !important;
    top: 0 !important;
    min-width: unset !important;
    max-width: unset !important;
    width: 100% !important;
    z-index: var(--z-index-higher, 9999) !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  }
}

.ticket-contact-results {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  max-height: 16rem;
  overflow-y: auto;
}

.ticket-associated-contacts {
  margin-top: var(--space-normal);
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
}

.ticket-associated-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small);
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius);
  background: var(--white);
}

.ticket-associated-checkbox {
  cursor: pointer;
  flex-shrink: 0;
  margin: 0;
}

.bulk-confirm-modal {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
}

.bulk-confirm-modal__title {
  font-size: var(--font-size-medium);
  font-weight: 700;
  margin: 0;
}

.bulk-confirm-modal__description {
  color: #374151;
  font-size: var(--font-size-small);
  margin: 0;
}

.bulk-confirm-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.ticket-associated-name {
  color: var(--s-900);
  font-weight: var(--font-weight-medium);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ticket-associated-actions {
  display: flex;
  align-items: center;
  gap: var(--space-micro);
}

.modal-header-centered {
  text-align: center;
  display: flex;
  justify-content: center;
  width: 100%;
}
</style>
